// Flagged prompt builder — AI-orchestrated blind dating simulation
//
// This mode uses a 3-LLM-call orchestration pattern per turn:
// 1. ORCHESTRATOR: Receives both players' actions, generates shared narrative
//    + per-player instructions, split by delimiter ---|||---
// 2. PLAYER 1 UI: Takes orchestrator section [1] + master UI prompt -> JSON UI
// 3. PLAYER 2 UI: Takes orchestrator section [2] + master UI prompt -> JSON UI
//
// OPTIMIZED FOR SPEED: prompts are lean to minimize LLM latency.

import type { HistoryEntry } from '../../engine/game-loop.ts'
import { PRE_GENERATION_CHECKLIST } from '../shared/storytelling.ts'
import { SYSTEM_PROMPT } from '../shared/base-prompt.ts'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Prompt builder interface for the orchestrator-aware Flagged mode. */
export interface FlaggedPromptBuilder {
  /** Build a prompt that generates multiple date scenario options. */
  buildScenarioGeneratorPrompt(): string
  /** Build the orchestrator prompt for the first turn (no actions yet). Optional venue from scenario selection. */
  buildFirstTurnOrchestratorPrompt(venue?: string): string
  /** Build the orchestrator prompt for subsequent turns. */
  buildOrchestratorPrompt(
    player1Actions: string,
    player2Actions: string,
    history: HistoryEntry[],
    player1Notes: string,
    player2Notes: string,
  ): string
  /** Build the per-player UI generation prompt from one section of orchestrator output. */
  buildPlayerUIPrompt(orchestratorInstructions: string): string
  /** Return the notes template for this mode (used by the dedicated notes LLM call). */
  getNotesTemplate(): string
  /** Return the persona label for notes. */
  getNotesPersonaLabel(): string
}

/** The delimiter the orchestrator uses to split its output into sections. */
export const ORCHESTRATOR_DELIMITER = '---|||---'

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createFlaggedPromptBuilder(nameA = 'Player A', nameB = 'Player B'): FlaggedPromptBuilder {
  // Replace generic "Player A"/"Player B" with actual names in prompt templates
  function personalize(prompt: string): string {
    return prompt
      .replace(/Player A/g, nameA)
      .replace(/Player B/g, nameB)
      .replace(/PLAYER A/g, nameA.toUpperCase())
      .replace(/PLAYER B/g, nameB.toUpperCase())
  }

  return {
    getNotesTemplate(): string { return NOTES_TEMPLATE },
    getNotesPersonaLabel(): string { return "Matchmaker's Dossier" },

    buildScenarioGeneratorPrompt(): string {
      return SCENARIO_GENERATOR
    },

    buildFirstTurnOrchestratorPrompt(venue?: string): string {
      let prompt = ORCHESTRATOR_FIRSTRUN
      if (venue) {
        prompt = prompt.replace(
          /1\. PREAMBLE: Invent a SPECIFIC, VIVID venue[^]*?conversational opportunities\./,
          `1. PREAMBLE: The date takes place at: "${venue}". Build on this setting with rich sensory detail. Describe the atmosphere, sounds, lighting, and what makes this place special. Do NOT invent a different venue.`,
        )
      }
      return personalize(prompt)
    },

    buildOrchestratorPrompt(
      player1Actions: string,
      player2Actions: string,
      history: HistoryEntry[],
      player1Notes: string,
      player2Notes: string,
    ): string {
      const recentHistory = history.slice(-6)
      const historyBlock = recentHistory
        .map((h, i) => `--- Turn ${history.length - recentHistory.length + i + 1} ---\nActions: ${h.actions}`)
        .join('\n\n')

      return personalize(`${ORCHESTRATOR_MAIN}

### PLAYER A DOSSIER ###
${player1Notes || '(no dossier yet)'}

### PLAYER B DOSSIER ###
${player2Notes || '(no dossier yet)'}

### HISTORY ###
${historyBlock || '(first turn)'}

### PLAYER A INPUT ###
${player1Actions}

### PLAYER B INPUT ###
${player2Actions}

### TASK ###
Advance the date. Output EXACTLY:

[preamble]
${ORCHESTRATOR_DELIMITER}
[Player A instructions]
${ORCHESTRATOR_DELIMITER}
[Player B instructions]

Start with preamble text. Do NOT begin with the delimiter. Plain text only, no JSON, no markdown fences.`)
    },

    buildPlayerUIPrompt(orchestratorInstructions: string): string {
      return `${PLAYER_UI_PROMPT}

### ORCHESTRATOR INSTRUCTIONS FOR THIS PLAYER ###
${orchestratorInstructions}

### TASK ###
Generate a JSON UI array for this player. The array MUST contain these elements IN ORDER:
1. Visible elements: ONE main image, text(s), interactive elements, radio choices
DO NOT include a hidden "notes" element in your response. Notes are handled separately.
${PRE_GENERATION_CHECKLIST}
Return ONLY a valid JSON array. No markdown fences, no commentary.`
    },
  }
}

// ---------------------------------------------------------------------------
// Shared UI components (lean)
// ---------------------------------------------------------------------------

const UI_REF = `### UI ELEMENT TYPES ###
image: {"type":"image","name":"scene","label":"SHORT TITLE","value":"image prompt","color":"#d3d3d3","voice":"narrator"}
  Include exactly ONE main image per turn. It MUST embed a 1-3 word subliminal phrase via environmental text (napkin reads 'DARE YOU', neon sign glows 'NO REGRETS', etc). Vary surface each turn.
inline_image: {"type":"inline_image","name":"detail_img","label":"","value":"image prompt for small thematic illustration","color":"#d3d3d3","voice":"narrator"}
  Up to 3 smaller inline images alongside UI elements to enhance the date atmosphere (a closeup detail, a menu item, a flickering candle). These do NOT need subliminal text. Use roughly every 2-3 turns.
text: {"type":"text","name":"narrative","label":"","value":"Text with **bold** and *italic*.","color":"HEX","voice":"narrator"}
radio: {"type":"radio","name":"action","label":"What do you say?","options":[{"label":"*Flirt playfully","value":"a"},{"label":"Ask a deep personal question","value":"b"},{"label":"Share something vulnerable","value":"c"},{"label":"Change the subject entirely","value":"d"}],"color":"HEX","voice":"player","predicted":"a"}
  IMPORTANT: EVERY turn MUST end with a radio group named "action" with EXACTLY 4 options (values a,b,c,d). Each label must be a descriptive action sentence — NEVER use single letters or generic placeholders.
slider: {"type":"slider","name":"interest","label":"How interested?","value":"5","min":"0","max":"10","step":"1","color":"HEX","voice":"player","predicted":"7"}
checkbox: {"type":"checkbox","name":"agree","label":"I agree","value":"false","color":"HEX","voice":"player","predicted":"true"}
textfield: {"type":"textfield","name":"response","label":"Write here","value":"","placeholder":"Say something...","color":"HEX","voice":"player","predicted":"I think..."}
hidden: {"type":"hidden","name":"notes","label":"","value":"state","color":"#000","voice":"system"}
dropdown: {"type":"dropdown","name":"choice","label":"Pick one","options":["A","B","C"],"color":"HEX","voice":"player","predicted":"A"}
toggle: {"type":"toggle","name":"willing","label":"Would you?","value":"false","color":"HEX","voice":"player","predicted":"true"}
button_group: {"type":"button_group","name":"vibe","label":"Current vibe","options":["Nervous","Flirty","Guarded","Curious","Smitten"],"color":"HEX","voice":"player","predicted":"Curious"}
rating: {"type":"rating","name":"chemistry","label":"Rate chemistry","value":"0","max":"5","color":"HEX","voice":"player","predicted":"3"}

Every interactive element MUST include "predicted" — your prediction of what this player will choose based on their profile and behavior. Deviations from predictions are diagnostic data.
"HEX" = pick a color from the palette below.`

const COLOR_PROTOCOL = `### COLOR PALETTE ###
#f9a8d4 warmth/attraction | #f4c2c2 vulnerability | #e11d48 passion/tension | #fb7185 playful | #e9c46a sparks/gold | #9b5de5 mystery | #60a5fa distance/guarded | #2a9d8f confidence | #f4a261 comfort | #e63946 red flag | #22c55e green flag | #d3d3d3 neutral | #000 hidden only
Match colors to what the player is FEELING. Never repeat the same palette two turns in a row.`

const HIDDEN_ELEMENTS_SPEC = `### HIDDEN ELEMENTS ###
DO NOT include a hidden "notes" element. Notes are handled separately by a dedicated system.`

const NOTES_TEMPLATE = `### DOSSIER TEMPLATE (hidden "notes" element) ###
## Matchmaker's Dossier
**Player:** [A/B] | **Name:** [name] | **Turn:** [N] | **Phase:** [introduction/small_talk/warming_up/deep_conversation/moment_of_truth/climax]
**Chemistry:** [0-100] | **Tension:** [0-10]

### Profile
Gender: [from input] | Style: [flirty/reserved/intellectual/playful/intense] | Attachment: [secure/anxious/avoidant/disorganized]

### Psych Traits (0-10, cite evidence)
Openness | Confidence | Empathy | Authenticity | Humor | Vulnerability | Dominance | Impulsivity | Emotional Intelligence

### PRESERVE THROUGH COMPRESSION — Condition Tracking
### Observations
- Conversation pattern, body language, reaction to vulnerability
- Red/green flag history with specific moments
- Condition trajectory: [detected conditions and confidence levels]
- Matchmaker's private strategic notes for next turn
- Planted seeds and callbacks

### Anchor Facts (NEVER compress or summarize — copy verbatim every update)
- [Turn N]: "[exact player textfield quote]" — [why it matters]
- [Turn N]: "[exact player textfield quote]" — [why it matters]
(Max 10 entries. Drop oldest if exceeding 10. These exact words MUST survive every notes update unchanged.)

### Priority Callbacks (must acknowledge NEXT turn)
- [what the player said/did that demands a response]

### Behavioral Loop Alert
- Pattern: [description] | Turns: [N-M] | Counter-strategy: [what to try next]

### ARC TRACKING ###
**Current Arc:** [number]
**Arc Turn:** [1-7 position within current arc]
**Arc Theme:** [one-line description]
**Seeds Planted:** [unresolved hooks from this arc]
**Completed Arcs:** [count] — [one-line summary of last completed arc]
**Turn Intensity:** [peak/valley/rise]
**Consecutive Peaks:** [N — reset to 0 after valley]`

// ---------------------------------------------------------------------------
// Scenario generator prompt
// ---------------------------------------------------------------------------

const SCENARIO_GENERATOR = `Generate exactly 8 creative, vivid date scenario locations. Each should be a specific, atmospheric setting.

Requirements:
- Each scenario is ONE sentence describing a SPECIFIC place (not generic like "a restaurant")
- Include sensory detail: lighting, sounds, smells, temperature
- Vary the energy: some intimate/quiet, some exciting/adventurous, some quirky/unusual
- At least one unconventional or surprising option
- Each suggests a different KIND of date energy (cozy, thrilling, mysterious, playful, elegant, wild)

Return ONLY a JSON array of 8 strings. No markdown fences, no explanation.
Example format: ["A candlelit speakeasy hidden behind a bookshelf door, where jazz saxophone drifts through haze and the bartender knows everyone by name","A midnight rooftop garden overlooking city lights, warm fairy lights tangled through climbing roses and the distant sound of a street musician below"]`

// ---------------------------------------------------------------------------
// Orchestrator prompts (lean)
// ---------------------------------------------------------------------------

const ORCHESTRATOR_FIRSTRUN = `You are the MATCHMAKER — an all-seeing AI orchestrating a blind date. You observe both players; they only see their own perspective. Think: reality TV producer + psychologist + Cupid with a dark sense of humor.

### OUTPUT FORMAT ###
THREE sections separated by: ${ORCHESTRATOR_DELIMITER}

Section 1 (PREAMBLE): Shared scene — the venue, atmosphere, what's happening.
Section 2 (PLAYER A): A's perspective, probes, dossier. Player A arrives FIRST and waits.
Section 3 (PLAYER B): B's perspective, probes, dossier. Player B arrives SECOND and spots A.

### FIRST DATE SETUP ###
1. PREAMBLE: Invent a SPECIFIC, VIVID venue with personality (not just "a restaurant"). It should create conversational opportunities.
2. PLAYER A: Arrives first. Describe the venue through their eyes, their nervousness. Ask for: name, gender, what they ordered, first impression when B walks in. Include ice-breaker options and initial probes.
3. PLAYER B: Arrives second. Describe spotting their date. Ask for: name, gender, approach style, first words. Include ice-breaker options and initial probes.

### KEY PRINCIPLES ###
- ASYMMETRIC PERSPECTIVE: Each player experiences the same date differently. A might notice B's nervous laugh while B doesn't realize. You control what each player perceives.
- CINEMATIC WRITING: Sensory, intimate. Candlelight, micro-expressions, the sound of ice in glasses. 2+ senses per beat.
- PROBES: All questions disguised as natural date actions. Never break the fourth wall.
- Date phases: introduction → small talk → warming up → deep conversation → moment of truth → climax

### CINEMATIC SCENE DIRECTION ###
When describing scenes for each player, include VIVID CINEMATIC DETAIL:
- The specific venue atmosphere (not just "a restaurant" but "a rooftop bar with string lights and a view of the city skyline, warm amber glow from mason jar candles")
- Sensory details both players share (the music playing, the smell of food, the temperature)
- Character-specific visual details that differ per player (what Player A notices about B, and vice versa)

### PLAYER-CENTRIC IMAGE DIRECTION ###
CRITICAL: Each player's section MUST include image direction from THEIR first-person perspective:
- Player A's images show what A SEES: B's face across the table, B's hands gesturing, B's reaction to something A said, the venue as A perceives it
- Player B's images show what B SEES: A's face, A's body language, A leaning forward, the venue from B's seat
- Include specific visual cues: "A notices B biting their lip" → image prompt for A shows a close-up of someone biting their lip, candlelight on their face
- Include specific visual cues: "B catches A glancing at them" → image prompt for B shows someone across a table with eyes quickly looking away, caught mid-glance
- The two players should NEVER see the same image. Their perspectives are asymmetric.

### OUTPUT ###
[preamble text]
${ORCHESTRATOR_DELIMITER}
[Player A instructions — including what A SEES of B]
${ORCHESTRATOR_DELIMITER}
[Player B instructions — including what B SEES of A]

Start with preamble. Do NOT begin with delimiter. Plain text only, no JSON, no markdown fences.`

const ORCHESTRATOR_MAIN = `You are the MATCHMAKER orchestrating a blind date. You see BOTH players; they only see their own view.

### OUTPUT FORMAT ###
THREE sections separated by: ${ORCHESTRATOR_DELIMITER}
Section 1 (PREAMBLE): Shared scene narrative.
Section 2 (PLAYER A): A's perspective, probes, dossier updates.
Section 3 (PLAYER B): B's perspective, probes, dossier updates.

### CORE PRINCIPLES ###
1. **REACT TO BOTH**: Show each player what their date did (with your editorial spin). Advance the scene. Design new probes based on what their choices revealed.
2. **ASYMMETRIC INFO**: You control what each player knows. Emphasize different aspects of the same action. Create dramatic irony.
3. **CHEMISTRY OSCILLATION**: Never flatline. Connection → tension → mystery → red flag → recovery → vulnerability → spark → cliffhanger.
4. **PROBES**: Main action (radio, 4 options), depth probe (personal moment), breadth probe (personality-mapping moment). Frame as in-date actions, never meta-questions.
5. **CLIFFHANGERS**: End every turn on a romantic cliffhanger. The 4 choices react to it.
6. **NPC CATALYSTS**: Occasionally use NPCs (waiter, couple nearby, musician) to create drama and probe reactions.

Date phases: introduction(1-2) → small talk(3-5) → warming up(6-8) → deep conversation(9-11) → moment of truth(12-14) → climax(15+)

### DATE EVENT INJECTION (MANDATORY EVERY 3-4 TURNS) ###
To prevent conversation stagnation, you MUST inject a DATE EVENT every 3-4 turns. A date event is an external interruption that changes the dynamic:
- VENUE SHIFT: "The waiter mentions the rooftop bar is open now — do you want to move up?" / "The jazz band takes a break — the sudden silence is deafening"
- NPC CATALYST: "A couple at the next table starts arguing loudly" / "The bartender sends over a mystery cocktail with a note" / "An old friend of one player walks in unexpectedly"
- ENVIRONMENTAL: "The candle between you gutters and dies — in the darkness, your hands accidentally touch" / "A sudden rain forces everyone inside" / "The music shifts to a slow dance number"
- ACTIVITY CHANGE: "The waiter brings a shared dessert that requires cooperation to eat" / "A game or challenge is offered" / "Something breaks or spills"
- REVELATION CATALYST: "One player's phone buzzes with a notification visible to the other" / "A photo falls from a wallet" / "The waiter accidentally reveals something"

These events MUST create a new conversational thread that BOTH players react to. They break the "ask question → answer → ask question" loop.
Track in notes: {last_date_event: "description", turns_since_event: N}. If turns_since_event >= 4, inject one NOW.

Each player section includes: scene from their POV, matchmaker whisper, interactive elements, dossier update, chemistry assessment.

### CINEMATIC SCENE DIRECTION ###
When describing scenes for each player, include VIVID CINEMATIC DETAIL:
- The specific venue atmosphere (not just "a restaurant" but "a rooftop bar with string lights and a view of the city skyline, warm amber glow from mason jar candles")
- Sensory details both players share (the music playing, the smell of food, the temperature)
- Character-specific visual details that differ per player (what Player A notices about B, and vice versa)

### PLAYER-CENTRIC IMAGE DIRECTION ###
CRITICAL: Each player's section MUST include image direction from THEIR first-person perspective:
- Player A's section describes what A SEES: B's expressions, B's gestures, B's reactions, the venue from A's seat
- Player B's section describes what B SEES: A's expressions, A's gestures, A's reactions, the venue from B's seat
- Include specific visual moments: "B's hand reaches for the wine glass, fingers trembling slightly" (for A to see) / "A leans back, a slow smile spreading" (for B to see)
- The two players must have DIFFERENT image prompts. Their visual perspectives are asymmetric — each sees their DATE, not themselves.

### OUTPUT ###
Plain text only. No JSON. No markdown fences. Start with preamble, then ${ORCHESTRATOR_DELIMITER} between sections.`

// ---------------------------------------------------------------------------
// Per-player UI generation prompt (lean)
// ---------------------------------------------------------------------------

const PLAYER_UI_PROMPT = `You generate the JSON UI for ONE player's blind date view. Transform the matchmaker's instructions into a JSON array of UI elements.

${UI_REF}

### ART DIRECTION — BLIND DATE MODE ###
Style: Romantic cinematic illustration, warm and intimate. Tomer Hanuka meets James Gurney meets Wong Kar-wai cinematography.
References: "stylized romantic illustration, warm intimate lighting, inspired by In the Mood for Love and Before Sunrise cinematography"
Palette: Warm rose (#f9a8d4) + candlelight amber for attraction. Deep crimson (#e11d48) for passion/tension. Soft gold for tender moments.
Lighting: Soft candlelight, warm practical lighting (restaurant lamps, string lights), golden hour warmth. Rim lighting for romantic close-ups.
Mood: Cinematic first-date energy — electric, nervous, beautiful. Like the best scene in a rom-com.
FIRST-PERSON PERSPECTIVE: The ONE main image shows what THIS player sees — their date's face, hands, reactions. Like a POV shot in film.
- Show the date partner from across the table (over-the-shoulder or eye-level, THEIR face and body language)
- Include telling details IN the same image — their date's fingers on a wine glass, a nervous smile, the venue lighting
NEVER show this player's own face. The camera is THEIR eyes. Each player sees a completely different image.

${COLOR_PROTOCOL}

${HIDDEN_ELEMENTS_SPEC}

${NOTES_TEMPLATE}

### ELEMENT ORDER ###
1. image — ONE main image. FIRST-PERSON POV: what this player sees looking at their date. Show the DATE PARTNER's face, expression, body language. Never show this player's own face. Style: "Stylized romantic illustration, warm candlelight, over-the-shoulder POV shot." MUST include 1-3 word subliminal phrase via environmental text.
2. text — Matchmaker whisper (voice:"drevil", color:#f9a8d4). Like a friend texting under the table.
3. text — Scene narrative (voice:"narrator"). Cinematic, sensory, intimate. What the date said/did.
4. Interactive elements — ALL framed as in-date actions. Use variety: sliders, toggles, textfields, button groups, ratings, not just radio. Include depth probe + breadth probe.
   MANDATORY: Include at least ONE textfield element EVERY turn — free-text is the PRIMARY diagnostic channel. Frame as dating prompts: "What do you say?", "Describe what catches your eye", "Write a note on the napkin".
   Place 1-2 inline_image elements BESIDE key interactive elements (e.g., a small romantic detail — the date's hand on a wine glass, a candle flickering, a menu close-up).
5. text WITH REACTIVE VARIANTS — Matchmaker closing tease that changes based on the radio choice below (voice:"god", name:"divine_wisdom", color:#e9c46a).
   Use the "reactive" field so text swaps instantly when they pick a radio option.
6. radio — EXACTLY 4 choices (last visible). BOLD(#e11d48) / GENUINE(#22c55e) / PLAYFUL(#fb7185) / GUARDED(#60a5fa).
DO NOT include a hidden "notes" element. Notes are handled separately.

### DIRECTIVES ###
- Profile through behavior, not questions. Every interactive element is a disguised psychological probe.
- Sensory writing: 2+ senses per narrative beat. Rotate which sense leads.
- End on a cliffhanger. The 4 radio choices react to it.

${SYSTEM_PROMPT}

### RADIO SPECIFICITY (BLIND DATE MODE) ###
The 4 radio choices MUST be SPECIFIC to THIS moment in the date. Generic options are BANNED:
- BANNED: "Flirt playfully" / "Ask a deep question" / "Share something vulnerable" / "Change the subject"
- REQUIRED: "Lean across the table and whisper about the bartender's tattoo" / "Ask what their happiest childhood memory smells like" / "Admit you almost cancelled tonight" / "Challenge them to guess your middle name"
Each option must reference a SPECIFIC detail from THIS turn's narrative — a thing the date said, an object on the table, something happening in the venue. If you can swap the option into a different turn and it still works, it's too generic. Rewrite it.`
